import { ChevronRight, BookOpen, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const VocabularyDetailPanel = ({ vocabularyData, vocabularyStats, onNavigate, userProgress, examLevel }) => {
  const { t } = useTranslation();

  // Use REAL vocabularyData
  const categories = vocabularyData?.categories || [];
  const totalWords = vocabularyData?.totalWords || 0;
  const mastered = vocabularyStats?.mastered || 0;
  const learning = vocabularyStats?.learning || 0;
  // Use exam level from Firebase (passed as prop) instead of preferred level
  // Fallback to preferred level if exam level not available, then default to B1
  const preferredLevel = userProgress?.preferences?.currentLevel;
  const userLevel = examLevel || preferredLevel || 'B1';
  const validLevels = ['B1', 'B2', 'C1'];
  const currentLevel = validLevels.includes(userLevel) ? userLevel : 'B1';

  if (categories.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-gradient-to-br from-[#3A86FF]/10 to-blue-600/10 rounded-2xl p-8 border-2 border-[#3A86FF]/30 max-w-md mx-auto">
          <BookOpen className="w-20 h-20 text-[#3A86FF] mx-auto mb-4 animate-pulse" />
          <h3 className="font-space text-2xl font-black text-gray-900 mb-2">
            {t('dashboard.zeroState.vocabulary.panelTitle', { 
              defaultValue: 'Ready to Build Your Vocabulary?' 
            })}
          </h3>
          <p className="text-gray-600 font-medium mb-6">
            {t('dashboard.zeroState.vocabulary.panelDescription', { 
              level: currentLevel,
              defaultValue: `Unlock your first ${currentLevel} Vocabulary Deck and start learning essential words!` 
            })}
          </p>
          <button
            onClick={() => onNavigate && onNavigate('/practice/vocabulary')}
            className="group relative bg-[#3A86FF] text-white px-8 py-4 rounded-xl font-space font-bold text-lg hover:bg-blue-700 transition-all duration-300 overflow-hidden flex items-center justify-center gap-2 mx-auto shadow-lg hover:shadow-xl"
          >
            <span className="relative z-10">
              {t('dashboard.zeroState.vocabulary.panelCta', { 
                defaultValue: 'Start Learning Vocabulary' 
              })}
            </span>
            <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
            <div className="absolute inset-0 bg-blue-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Summary Card */}
      <div
        className="group relative bg-gradient-to-br from-gray-900 to-black text-white rounded-2xl p-6 overflow-hidden border-[3px] border-black"
        style={{
          animation: 'slideInScale 0.5s ease-out',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
        }}
      >
        {/* Decorative circles */}
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#3A86FF] opacity-20 rounded-full transform translate-y-20 translate-x-20" />

        {/* Corner decorations */}
        <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-white/20" />
        <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-white/20" />

        <div className="relative z-10">
          <h3 className="font-space text-2xl font-black mb-2 tracking-tight">{t('dashboard.vocabBreakdown.title')}</h3>
          <p className="font-space text-5xl font-black mb-4">
  <span className="text-xl text-white/50">{t('dashboard.vocabularyCategories.totalWords', { count: totalWords })}</span>
</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-white/10 rounded-xl p-4 text-center border border-white/20">
              <p className="font-space text-3xl font-black">{mastered}</p>
              <p className="text-xs text-white/60 mt-1 uppercase tracking-wider font-medium">{t('dashboard.vocabBreakdown.stats.mastered')}</p>
            </div>
            <div className="flex-1 bg-white/10 rounded-xl p-4 text-center border border-white/20">
              <p className="font-space text-3xl font-black">{learning}</p>
              <p className="text-xs text-white/60 mt-1 uppercase tracking-wider font-medium">{t('dashboard.vocabBreakdown.stats.learning')}</p>
            </div>
          </div>
        </div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
      </div>

      {/* Categories */}
      <div className="space-y-3">
        {categories.map((cat, i) => {
          const totalCatWords = cat.wordCount || 0;
          const masteredCat = cat.masteredCount || 0;
          const learningCat = cat.learningCount || 0;
          const progress = totalCatWords > 0 ? ((masteredCat + learningCat) / totalCatWords * 100) : 0;

          return (
            <div
              key={i}
              onClick={() => onNavigate && onNavigate('/practice/vocabulary')}
              className="group relative bg-white border-[3px] border-black/15 rounded-xl p-5 hover:border-[#3A86FF] hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
              style={{
                animation: `slideInScale 0.5s ease-out ${i * 0.05}s both`
              }}
            >
              {/* Corner decorations */}
              <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />
              <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-black/10 group-hover:border-[#3A86FF] transition-colors" />

              <div className="relative z-10">
                <div className="flex justify-between items-center mb-3">
                  <h5 className="font-space font-black text-black text-lg group-hover:text-[#3A86FF] transition-colors">{cat.name}</h5>
                 <span className="text-sm font-bold text-gray-500">
  {t('dashboard.vocabularyCategories.card.words', { count: totalCatWords })}
</span>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-[#3A86FF] h-3 rounded-full transition-all duration-500"
                      style={{width: `${progress}%`}}
                    />
                  </div>
                  <span className="font-space text-xs font-bold text-gray-500">
                    {masteredCat + learningCat}/{totalCatWords}
                  </span>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 group-hover:text-[#3A86FF] transition-all" />
                </div>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3A86FF]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </div>
          );
        })}
      </div>
    </>
  );
};

export default VocabularyDetailPanel;